import React, { useEffect, useState } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import {
  type EstimateOwnerFlagImpactInput,
  estimateOwnerFlagImpact,
} from '../../../services/ownerConsoleService'
import { type FormattedFlagImpact, formatImpact } from './ownerFlagsModel'

export interface OwnerFlagImpactPreviewProps {
  input: EstimateOwnerFlagImpactInput
}


export const OwnerFlagImpactPreview: React.FC<OwnerFlagImpactPreviewProps> = ({ input }) => {
  const { t } = useNls()
  const inputKey = JSON.stringify(input)
  const [loadedKey, setLoadedKey] = useState<string | null>(null)
  const [previewData, setPreviewData] = useState<{ error: boolean; impact: FormattedFlagImpact | null }>({
    error: false,
    impact: null,
  })

  useEffect(() => {
    let cancelled = false
    const parsedInput: EstimateOwnerFlagImpactInput = JSON.parse(inputKey)

    estimateOwnerFlagImpact(parsedInput)
      .then((res) => {
        if (!cancelled) {
          setPreviewData({
            error: false,
            impact: formatImpact(res),
          })
          setLoadedKey(inputKey)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPreviewData({
            error: true,
            impact: null,
          })
          setLoadedKey(inputKey)
        }
      })

    return () => {
      cancelled = true
    }
  }, [inputKey])

  const isLoading = loadedKey !== inputKey

  if (isLoading) {
    return (
      <span className="ownerFlagsImpactBlock">
        <span className="ownerFlagsImpactLoading">
          {t('NLSOwnerConsoleFlagsImpactLoading')}
        </span>
      </span>
    )
  }

  if (previewData.error || !previewData.impact) {
    return (
      <span className="ownerFlagsImpactBlock">
        <span className="ownerFlagsImpactError">
          {t('NLSOwnerConsoleFlagsImpactError')}
        </span>
      </span>
    )
  }

  const { impact } = previewData

  return (
    <span className="ownerFlagsImpactBlock">
      <span className="ownerFlagsImpactSummary">
        {t('NLSOwnerConsoleFlagsImpactSummary', {
          turnsOn: impact.turnsOn,
          turnsOff: impact.turnsOff,
          scanned: impact.scannedText,
        })}
      </span>
      {impact.dependents.length > 0 && (
        <span className="ownerFlagsImpactDependentsList">
          {impact.dependents.map((dep) => (
            <span key={dep.key} className="ownerFlagsImpactDependent">
              {t('NLSOwnerConsoleFlagsImpactDependent', {
                key: dep.key,
                turnsOn: dep.turnsOn,
                turnsOff: dep.turnsOff,
              })}
            </span>
          ))}
        </span>
      )}
    </span>
  )
}
